import { Button, Flex, Modal, Tabs, Text, Textarea } from "@mantine/core";
import { IconCode, IconSparkles } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import {
  ADMIN_INTENT_ALL_OPTIONS_JSON_TEMPLATE,
  ADMIN_INTENT_GPT_INSTRUCTIONS,
  ADMIN_INTENT_SELECTED_OPTIONS_JSON_TEMPLATE,
  adminIntentQuestionsForImport,
  unansweredAdminIntentQuestionIds,
  parseAdminIntentSelectedJson,
  selectedJsonToIntentAnswers,
  overlaySelectedOntoUpdateItems,
  stringifyAdminIntentAllOptionsJson,
  stringifyAdminIntentSelectedOptionsJson,
  intentQuestionsToJsonSource,
  type AdminIntentJsonQuestionSource,
} from "@shared/adminIntentQuestionnaireJson";
import type { IntentQuestion } from "@shared/companyIntent.types";
import trpc from "@/shared/utils/trpc";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";
import { notifications } from "@mantine/notifications";

type AdminIntentQuestionnaireJsonModalProps = {
  opened: boolean;
  onClose: () => void;
  companyId: string;
  items: AdminIntentJsonQuestionSource[];
  additionalInfo: string;
  submitMode: "submit" | "update";
  canImportQuestions?: boolean;
  onApplied: () => void;
  onReload?: () => void;
  zIndex?: number;
};

export default function AdminIntentQuestionnaireJsonModal({
  opened,
  onClose,
  companyId,
  items,
  additionalInfo,
  submitMode,
  canImportQuestions = false,
  onApplied,
  onReload,
  zIndex = 1300,
}: AdminIntentQuestionnaireJsonModalProps) {
  const [allOptionsText, setAllOptionsText] = useState("");
  const [selectedText, setSelectedText] = useState("");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [allOptionsHint, setAllOptionsHint] = useState<string | null>(null);
  const [parseHint, setParseHint] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setAllOptionsText(
      items.length > 0
        ? stringifyAdminIntentAllOptionsJson(items)
        : ADMIN_INTENT_ALL_OPTIONS_JSON_TEMPLATE,
    );
    setSelectedText(
      items.length > 0
        ? stringifyAdminIntentSelectedOptionsJson(items, additionalInfo)
        : ADMIN_INTENT_SELECTED_OPTIONS_JSON_TEMPLATE,
    );
    setAllOptionsHint(null);
    setParseHint(null);
  }, [opened, items, additionalInfo]);

  const handleClose = () => {
    if (saving || generating) return;
    setAllOptionsHint(null);
    setParseHint(null);
    onClose();
  };

  const copyText = async (text: string, title: string, message: string) => {
    try {
      await navigator.clipboard.writeText(text);
      notifications.show({ title, message, color: "blue" });
    } catch {
      notifications.show({
        title: "Copy failed",
        message: "Clipboard was unavailable. Select the JSON and copy it manually.",
        color: "yellow",
      });
    }
  };

  const handleGenerate = async () => {
    setAllOptionsHint(null);
    setParseHint(null);
    setGenerating(true);
    try {
      const res = await trpc.admin.generateCompanyIntentQuestionnaire.mutate({ companyId });
      const snapshot = res.questionsSnapshot as { questions?: IntentQuestion[] } | null | undefined;
      const questions = Array.isArray(snapshot?.questions) ? snapshot.questions : [];
      const source = intentQuestionsToJsonSource(questions, {});
      setAllOptionsText(stringifyAdminIntentAllOptionsJson(source));
      setSelectedText(stringifyAdminIntentSelectedOptionsJson(source, additionalInfo));
      successNotification({
        title: "Questions generated as JSON",
        message: "This is the same generate output as the card UI. Copy it, or mark selected and submit.",
      });
      onReload?.();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setGenerating(false);
    }
  };

  const importQuestions = async (jsonText: string) => {
    const parsed = parseAdminIntentSelectedJson(jsonText);
    if (!parsed.ok) {
      return parsed;
    }
    await trpc.admin.importCompanyIntentQuestionnaireFromJson.mutate({
      companyId,
      questions: adminIntentQuestionsForImport(parsed.value.questions),
    });
    return parsed;
  };

  const handleApplyQuestions = async () => {
    setAllOptionsHint(null);
    setSaving(true);
    try {
      const result = await importQuestions(allOptionsText);
      if ("error" in result) {
        setAllOptionsHint(result.error);
        return;
      }
      successNotification({
        title: "Questions imported",
        message: "GPT JSON was saved as this company's 15 questions. Open the selected-options tab to submit answers.",
      });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const parsed = parseAdminIntentSelectedJson(selectedText);
    if (!parsed.ok) {
      setParseHint(parsed.error);
      return;
    }

    const missing = unansweredAdminIntentQuestionIds(parsed.value.questions);
    if (missing.length > 0) {
      setParseHint(
        `Set selected true on: ${missing.join(", ")}. Each question needs a chosen option before submit.`,
      );
      return;
    }

    setParseHint(null);
    setSaving(true);
    try {
      if (submitMode === "update") {
        await trpc.admin.updateCompanyIntentQuestionnaire.mutate({
          companyId,
          additionalInfo: parsed.value.additionalInfo.trim() || null,
          items: overlaySelectedOntoUpdateItems(items, parsed.value.questions),
        });
        successNotification({
          title: "Onboarding Q&A updated",
          message: "Selected options were saved from JSON. The company was not notified.",
        });
      } else {
        if (canImportQuestions) {
          await trpc.admin.importCompanyIntentQuestionnaireFromJson.mutate({
            companyId,
            questions: adminIntentQuestionsForImport(parsed.value.questions),
          });
        }
        await trpc.admin.submitCompanyIntentQuestionnaire.mutate({
          companyId,
          answers: selectedJsonToIntentAnswers(parsed.value.questions),
        });
        const extra = parsed.value.additionalInfo.trim();
        if (extra) {
          await trpc.admin.updateCompanyIntentQuestionnaire.mutate({
            companyId,
            additionalInfo: extra,
            items: overlaySelectedOntoUpdateItems(
              items.length > 0 ? items : parsed.value.questions,
              parsed.value.questions,
            ),
          });
        }
        successNotification({
          title: "Onboarding Q&A submitted",
          message: "Questions and selected answers were saved from JSON. The company was not notified.",
        });
      }
      onApplied();
      onClose();
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setSaving(false);
    }
  };

  const textareaStyles = {
    input: { fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 13 },
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Onboarding Q&A JSON"
      centered
      size="xl"
      zIndex={zIndex}
    >
      <Text fz="sm" c="slate.7" mb="sm">
        Generate in JSON uses the same in-app generator. The result is the generate shape: version,
        decisionSignals, followUp and options with id, label and tags. Copy that, or set selected
        true and submit.
      </Text>
      {canImportQuestions ? (
        <Flex justify="flex-end" mb="sm">
          <Button
            size="xs"
            leftSection={<IconSparkles size={14} />}
            onClick={() => void handleGenerate()}
            loading={generating}
            disabled={saving}
          >
            Generate in JSON
          </Button>
        </Flex>
      ) : null}
      <Tabs defaultValue="all-options">
        <Tabs.List>
          <Tabs.Tab value="all-options">Questions with all options</Tabs.Tab>
          <Tabs.Tab value="selected">Questions with selected options</Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="all-options" pt="sm">
          <Flex justify="flex-end" gap="xs" mb="xs" wrap="wrap">
            <Button
              variant="subtle"
              size="xs"
              onClick={() =>
                void copyText(
                  ADMIN_INTENT_GPT_INSTRUCTIONS,
                  "GPT instructions copied",
                  "Paste this first, then paste the JSON template.",
                )
              }
              disabled={saving}
            >
              Copy GPT instructions
            </Button>
            <Button
              variant="subtle"
              size="xs"
              onClick={() =>
                void copyText(
                  ADMIN_INTENT_ALL_OPTIONS_JSON_TEMPLATE,
                  "All-options template copied",
                  "Paste into GPT, then paste the filled JSON back here.",
                )
              }
              disabled={saving}
            >
              Copy template
            </Button>
            <Button
              variant="subtle"
              size="xs"
              onClick={() =>
                void copyText(
                  allOptionsText,
                  "Questions copied",
                  "This JSON includes every option.",
                )
              }
              disabled={saving || !allOptionsText}
            >
              Copy JSON
            </Button>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => {
                setAllOptionsText(
                  items.length > 0
                    ? stringifyAdminIntentAllOptionsJson(items)
                    : ADMIN_INTENT_ALL_OPTIONS_JSON_TEMPLATE,
                );
                setAllOptionsHint(null);
              }}
              disabled={saving}
            >
              Reset
            </Button>
          </Flex>
          <Textarea
            label="Questions JSON"
            placeholder="Paste GPT JSON with all 15 questions and 4 options each"
            minRows={16}
            autosize
            maxRows={28}
            value={allOptionsText}
            onChange={(event) => {
              setAllOptionsText(event.currentTarget.value);
              if (allOptionsHint) setAllOptionsHint(null);
            }}
            error={allOptionsHint}
            styles={textareaStyles}
          />
          {canImportQuestions ? (
            <Flex justify="flex-end" gap="sm" mt="lg">
              <Button
                leftSection={<IconCode size={16} />}
                onClick={() => void handleApplyQuestions()}
                loading={saving}
              >
                Import questions JSON
              </Button>
            </Flex>
          ) : (
            <Text fz="xs" c="dimmed" mt="sm">
              Submitted Q&A cannot replace questions from JSON. Copy this tab for GPT, then submit
              selected options on the next tab.
            </Text>
          )}
        </Tabs.Panel>
        <Tabs.Panel value="selected" pt="sm">
          <Flex justify="flex-end" gap="xs" mb="xs" wrap="wrap">
            <Button
              variant="subtle"
              size="xs"
              onClick={() =>
                void copyText(
                  ADMIN_INTENT_GPT_INSTRUCTIONS,
                  "GPT instructions copied",
                  "Paste this first, then paste the selected-options template.",
                )
              }
              disabled={saving}
            >
              Copy GPT instructions
            </Button>
            <Button
              variant="subtle"
              size="xs"
              onClick={() =>
                void copyText(
                  ADMIN_INTENT_SELECTED_OPTIONS_JSON_TEMPLATE,
                  "Selected-options template copied",
                  "Ask GPT to set selected true, then paste it back and submit.",
                )
              }
              disabled={saving}
            >
              Copy template
            </Button>
            <Button
              variant="subtle"
              size="xs"
              onClick={() =>
                void copyText(
                  selectedText,
                  "Selected-options JSON copied",
                  "Set selected to true, then paste it back and submit.",
                )
              }
              disabled={saving || !selectedText}
            >
              Copy JSON
            </Button>
            <Button
              variant="subtle"
              size="xs"
              onClick={() => {
                setSelectedText(
                  items.length > 0
                    ? stringifyAdminIntentSelectedOptionsJson(items, additionalInfo)
                    : ADMIN_INTENT_SELECTED_OPTIONS_JSON_TEMPLATE,
                );
                setParseHint(null);
              }}
              disabled={saving}
            >
              Reset
            </Button>
          </Flex>
          <Textarea
            label="Selected options JSON"
            placeholder="Paste GPT JSON with selected true on chosen options"
            minRows={16}
            autosize
            maxRows={28}
            value={selectedText}
            onChange={(event) => {
              setSelectedText(event.currentTarget.value);
              if (parseHint) setParseHint(null);
            }}
            error={parseHint}
            styles={textareaStyles}
          />
          <Flex justify="flex-end" gap="sm" mt="lg">
            <Button variant="default" onClick={handleClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              leftSection={<IconCode size={16} />}
              onClick={() => void handleSubmit()}
              loading={saving}
            >
              Submit JSON
            </Button>
          </Flex>
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}
